import { MM_PER_INCH } from './pageUnits';
import type {
  PageOrientation,
  PagePresetId,
  PageUnits,
} from './layoutTypes';

export type PagePresetFamily = 'iso' | 'ansi' | 'architectural';

export interface PagePreset {
  readonly id: Exclude<PagePresetId, 'custom'>;
  readonly family: PagePresetFamily;
  readonly orientation: PageOrientation;
  readonly name: string;
  readonly widthMm: number;
  readonly heightMm: number;
  readonly units: PageUnits;
  readonly displayDimensions: string;
}

export interface PagePresetGroup {
  readonly id: string;
  readonly label: string;
  readonly presets: readonly PagePreset[];
}

interface BasePage {
  readonly id: string;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly units: PageUnits;
}

const ISO: readonly BasePage[] = [
  { id: 'a5', name: 'A5', width: 148, height: 210, units: 'mm' },
  { id: 'a4', name: 'A4', width: 210, height: 297, units: 'mm' },
  { id: 'a3', name: 'A3', width: 297, height: 420, units: 'mm' },
  { id: 'a2', name: 'A2', width: 420, height: 594, units: 'mm' },
  { id: 'a1', name: 'A1', width: 594, height: 841, units: 'mm' },
  { id: 'a0', name: 'A0', width: 841, height: 1189, units: 'mm' },
];

const ANSI: readonly BasePage[] = [
  { id: 'letter', name: 'Letter', width: 8.5, height: 11, units: 'in' },
  { id: 'legal', name: 'Legal', width: 8.5, height: 14, units: 'in' },
  { id: 'tabloid', name: 'Tabloid', width: 11, height: 17, units: 'in' },
  { id: 'ansi-c', name: 'ANSI C', width: 17, height: 22, units: 'in' },
  { id: 'ansi-d', name: 'ANSI D', width: 22, height: 34, units: 'in' },
  { id: 'ansi-e', name: 'ANSI E', width: 34, height: 44, units: 'in' },
];

const ARCHITECTURAL: readonly BasePage[] = [
  { id: 'architectural-a', name: 'Architectural A', width: 9, height: 12, units: 'in' },
  { id: 'architectural-b', name: 'Architectural B', width: 12, height: 18, units: 'in' },
  { id: 'architectural-c', name: 'Architectural C', width: 18, height: 24, units: 'in' },
  { id: 'architectural-d', name: 'Architectural D', width: 24, height: 36, units: 'in' },
  { id: 'architectural-e', name: 'Architectural E', width: 36, height: 48, units: 'in' },
];

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1)));
}

function preciseMm(value: number): number {
  return Number(value.toFixed(3));
}

function presetsFor(
  family: PagePresetFamily,
  pages: readonly BasePage[],
  orientation: PageOrientation,
): PagePreset[] {
  return pages.map((page) => {
    const portraitWidthMm = page.units === 'mm' ? page.width : page.width * MM_PER_INCH;
    const portraitHeightMm = page.units === 'mm' ? page.height : page.height * MM_PER_INCH;
    const landscape = orientation === 'landscape';
    const widthMm = preciseMm(landscape ? portraitHeightMm : portraitWidthMm);
    const heightMm = preciseMm(landscape ? portraitWidthMm : portraitHeightMm);
    const displayWidth = landscape ? page.height : page.width;
    const displayHeight = landscape ? page.width : page.height;
    return {
      id: `${page.id}-${orientation}` as Exclude<PagePresetId, 'custom'>,
      family,
      orientation,
      name: page.name,
      widthMm,
      heightMm,
      units: page.units,
      displayDimensions:
        page.units === 'mm'
          ? `${formatNumber(displayWidth)} mm × ${formatNumber(displayHeight)} mm`
          : `${formatNumber(displayWidth)}″ × ${formatNumber(displayHeight)}″`,
    };
  });
}

export const PAGE_PRESET_GROUPS: readonly PagePresetGroup[] = [
  {
    id: 'iso-portrait',
    label: 'ISO — Portrait',
    presets: presetsFor('iso', ISO, 'portrait'),
  },
  {
    id: 'iso-landscape',
    label: 'ISO — Landscape',
    presets: presetsFor('iso', ISO, 'landscape'),
  },
  {
    id: 'ansi-portrait',
    label: 'ANSI — Portrait',
    presets: presetsFor('ansi', ANSI, 'portrait'),
  },
  {
    id: 'ansi-landscape',
    label: 'ANSI — Landscape',
    presets: presetsFor('ansi', ANSI, 'landscape'),
  },
  {
    id: 'architectural-portrait',
    label: 'Architectural — Portrait',
    presets: presetsFor('architectural', ARCHITECTURAL, 'portrait'),
  },
  {
    id: 'architectural-landscape',
    label: 'Architectural — Landscape',
    presets: presetsFor('architectural', ARCHITECTURAL, 'landscape'),
  },
];

export const PAGE_PRESETS: Readonly<
  Record<Exclude<PagePresetId, 'custom'>, PagePreset>
> = Object.fromEntries(
  PAGE_PRESET_GROUPS.flatMap((group) =>
    group.presets.map((preset) => [preset.id, preset]),
  ),
) as Record<Exclude<PagePresetId, 'custom'>, PagePreset>;

export function pagePreset(id: Exclude<PagePresetId, 'custom'>): PagePreset {
  return PAGE_PRESETS[id];
}
